/* D51 -- persistent "Trip active · Tracking location" banner.
 *
 * Rendered globally from `App.js` so it is visible on every screen
 * while the pinger is active. Polls the pinger's `isPinging()` /
 * `currentTripId()` inspectors every 5s (cheap synchronous reads).
 * Displays duration + purpose (fetched once via the metadata
 * endpoint) and a subtle Sky-accent pulse so the employee knows
 * tracking is live and must keep the tab open.
 */
import React, { useEffect, useState } from "react";
import { MapPin, Radio } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { isPinging, currentTripId } from "@/lib/fieldTripPinger";

const POLL_MS = 5000;

function _fmtDuration(startedIso) {
  if (!startedIso) return "";
  const started = new Date(startedIso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - started) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function TripActiveBanner() {
  const [tripId, setTripId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [tick, setTick] = useState(0);   // heart-beat for the duration string

  useEffect(() => {
    const interval = setInterval(() => {
      const nowActive = isPinging();
      const nowTrip = currentTripId();
      if (nowActive && nowTrip !== tripId) setTripId(nowTrip);
      else if (!nowActive && tripId) setTripId(null);
      setTick((t) => t + 1);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [tripId]);

  useEffect(() => {
    if (!tripId) { setMeta(null); return; }
    api.get(`/field-trips/${tripId}`, { silent: true })
      .then(({ data }) => setMeta(data))
      .catch(() => setMeta(null));
  }, [tripId]);

  return (
    <AnimatePresence>
      {tripId && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          className="fixed top-2 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          data-testid="trip-active-banner">
          <div className="pointer-events-auto flex items-center gap-2.5 px-3.5 py-1.5
                          rounded-full border border-sky-500/50 bg-sky-500/10
                          backdrop-blur-md text-[11px] text-sky-100 shadow-xl">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full
                                 bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-300"></span>
            </span>
            <Radio className="h-3 w-3" />
            <span className="uppercase tracking-widest text-[9px] font-semibold">
              Trip active
            </span>
            {meta?.field_purpose && (
              <span className="text-sky-200/85 truncate max-w-[220px]"
                      title={meta.field_purpose}>
                · {meta.field_purpose}
              </span>
            )}
            <span className="text-sky-200/85 tabular-nums">
              · {_fmtDuration(meta?.started_at)}
            </span>
            <span className="text-sky-200/60 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Tracking
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
